<template>
  <div
      v-show="show"
      class="flex flex-column">
    <PageScalingCard
        :header="'product.search' | trans"
        :show="show">
      <template #content>
        <CommonList
            ref="productList"
            :categories="categories"
            empty-filter-text="frontend.searching"
            empty-text="product.no-results"
            error-text="product.load-failed"
            :fields="fields"
            :is-loading="isLoading"
            primary-key="_uid"
            :search-options="searchOptions"
            search-url="app_api_product_search"
            :selector="true"
            @deselected="onProductDeselected"
            @highlight="onProductHighlighted"
            @search="currentSearch = $event"
            @selected="onProductSelected">
          <template #cell(stock)="row">
            <ColoredStockNumber :stock="row.value"/>
          </template>
          <template #cell(actions)="row">
            <slot
                name="actions"
                v-bind="row"/>
          </template>
        </CommonList>
      </template>

      <template #footer>
        <div class="product-info d-flex w-100 px-2">
          <div class="flex-grow-1 px-2">
            <div class="row">
              <div class="title col-2">
                {{ 'product.description'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  {{ currentProduct.extended_description }}
                </template>
              </div>
              <div class="title col-2">
                {{ 'product.saleAmount'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  {{ currentProduct.sale_amount }}
                  {{
                    'product.sale-amount-unit.' + currentProduct.sale_amount_unit|trans({
                      plural: currentProduct.sale_amount > 1
                    })
                  }}
                </template>
              </div>
            </div>
            <div class="row">
              <div class="title col-2">
                {{ 'product.category.title'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  {{ currentProduct.category.name }}
                </template>
              </div>
              <div class="title col-2">
                {{ 'product.orderCode'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  {{ currentProduct.order_code }}
                </template>
              </div>
            </div>
            <div class="row">
              <div class="title col-2">
                {{ 'product.location'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  <template v-if="currentProduct._group">
                    -
                  </template>
                  <template v-else>
                    {{ currentProduct.location.name }}
                  </template>
                </template>
              </div>
              <div class="title col-2">
                {{ 'product.partNumber'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  <template v-if="currentProduct._group">
                    -
                  </template>
                  <template v-else>
                    {{ currentProduct.part_number }}
                  </template>
                </template>
              </div>
            </div>
            <div class="row">
              <div class="title col-2">
                {{ 'product.status'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  {{ currentProduct.status.name }}
                  <template v-if="currentProduct.on_order">
                    {{ 'product.on-order'|trans }}
                  </template>
                </template>
              </div>
              <div class="title col-2">
                {{ 'receipt.tax'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  <template v-if="currentProduct._group">
                    -
                  </template>
                  <template v-else>
                    {{ currentProduct.tax_category.percentage }}%
                  </template>
                </template>
              </div>
            </div>
            <div class="row">
              <div class="title col-2">
                {{ 'product.supplier'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  <template v-if="currentProduct._group">
                    -
                  </template>
                  <template v-else>
                    {{ currentProduct.supplier.name }}
                  </template>
                </template>
              </div>
              <div class="title col-2">
                {{ 'product.externalLink'|trans }}
              </div>
              <div class="col-4">
                <template v-if="currentProduct">
                  <a
                      v-if="!!currentProduct.external_link"
                      :href="currentProduct.external_link"
                      target="_blank">
                    {{ 'general.click'|trans }}
                  </a>
                  <span v-else>-</span>
                </template>
              </div>
            </div>
          </div>
          <div class="product-info-img align-self-center ml-1">
            <template v-if="currentProduct && currentProduct.image">
              <img
                  :alt="currentProduct.order_code"
                  :src="currentProduct.image">
            </template>
          </div>
        </div>
      </template>
    </PageScalingCard>

    <KeyCapture
        :enabled="show"
        @keydown.esc.prevent.stop="onCancel"
        @keydown.f1.prevent.stop="addProduct"
        @keydown.f2.prevent.stop="editProductPage"
        @keydown.f3.prevent.stop="openProductPage"
        @keydown.f8.prevent.stop="importCsv"
        @keydown.insert.prevent.stop="focusSearch"/>

    <slot name="buttons">
      <b-button-toolbar
          v-if="forSale || forPurchase"
          justify>
        <b-button-group
            class="px-0 pr-md-1 col-md-4 col-sm-12 mb-sm-1"
            vertical>
          <template v-if="forPurchase">
            <b-button
                variant="light"
                @click="importCsv">
              <i class="fas fa-fw fa-file-csv"/> {{ 'purchase.import'|trans }} (F8)
            </b-button>
          </template>
          <b-button
              :disabled="currentSearch === '' && cancelDisabled"
              variant="danger"
              @click="onCancel">
            <i class="fas fa-fw fa-times"/>
            {{ currentSearch === '' ? 'form.cancel' : 'frontend.clear-search' | trans }} (esc)
          </b-button>
        </b-button-group>

        <b-button-group
            class="px-0 px-md-1 col-md-4 col-sm-12 mb-sm-1"
            vertical>
          <b-button
              v-if="forSale"
              variant="light"
              @click="openProductPage">
            <i class="fas fa-fw fa-book-open"/> {{ 'sale.show-details'|trans }} (F3)
          </b-button>
          <template v-if="forPurchase">
            <b-button
                variant="light"
                @click="editProductPage">
              <i class="fas fa-fw fa-edit"/> {{ 'sale.edit-details'|trans }} (F2)
            </b-button>
            <b-button
                variant="light"
                @click="openProductPage">
              <i class="fas fa-fw fa-book-open"/> {{ 'sale.show-details'|trans }} (F3)
            </b-button>
          </template>
        </b-button-group>

        <b-button-group
            class="px-0 pl-md-1 col-md-4 col-sm-12"
            vertical>
          <b-button
              v-if="forPurchase"
              variant="light"
              @click="addProduct">
            <i class="fas fa-fw fa-plus"/> {{ 'purchase.add-product'|trans }} (F1)
          </b-button>
          <b-button
              variant="light"
              @click="focusSearch">
            <i class="fas fa-fw fa-search"/> {{ 'product.search'|trans }} (ins)
          </b-button>
        </b-button-group>
      </b-button-toolbar>
    </slot>
  </div>
</template>

<script lang="ts">
import ProductGroup from '@/api/ProductGroup';
import ColoredStockNumber from '@/common/ColoredStockNumber.vue';
import productSalePrice from '@/products/ProductSalePrice';
import {BvTableFieldArray} from 'bootstrap-vue';
import {Component, Emit, Prop, Ref, Vue, Watch} from 'vue-property-decorator';
import KeyCapture from '@/common/KeyCapture.vue';
import PageScalingCard from '@/common/PageScalingCard.vue';
import CommonList from '@/common/CommonList.vue';
import Product from '@/api/Product';
import ProductCategory from '@/api/ProductCategory';
import {AxiosResponse} from 'axios';

@Component({
  components: {
    ColoredStockNumber,
    CommonList,
    PageScalingCard,
    KeyCapture,
  },
})
export default class SearchProductPage extends Vue {
  @Prop({default: true, type: Boolean})
  public readonly show!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly forSale!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly forPurchase!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly includeProductGroups!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly rowActions!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly cancelDisabled!: boolean;

  @Prop({default: false, type: Boolean})
  public readonly focusOnMount!: boolean;

  protected currentSearch = '';

  protected currentProduct: Product | ProductGroup | null = null;

  protected productIsSelected = false;

  protected productInfoShown = false;

  protected categories: ProductCategory[] = [];

  protected isLoading = true;

  /** The fields to be shown in the searcher table */
  protected fields: BvTableFieldArray = [
    {
      key: 'thumb',
      label: '',
      sortable: false,
      class: 'shrink d-none d-md-table-cell',
    },
    {
      key: 'category.name',
      label: this.$translator.trans('product.category.title'),
      sortable: true,
      class: 'actions category',
    },
    {
      key: 'extended_description',
      label: this.$translator.trans('product.description'),
      sortable: true,
      class: 'description',
    },
    {
      key: 'status.short',
      label: this.$translator.trans('product.stock-type'),
      sortable: true,
      class: 'actions status text-left text-md-center',
      formatter: (value, key, item?: Product) => value + (item?.on_order ? ' [O]' : ''),
      sortByFormatted: true,
    },
    {
      key: 'stock',
      label: this.$translator.trans('product.stock'),
      sortable: true,
      class: 'actions stock text-right',
    },
    {
      key: 'sale_price',
      label: this.$translator.trans('product.price'),
      sortable: true,
      class: 'actions price text-right',
      formatter: (value, key, item) => parseFloat(productSalePrice(item).toFixed()),
      sortByFormatted: true,
    },
  ];

  @Ref()
  private readonly productList!: CommonList;

  // noinspection JSUnusedGlobalSymbols Vue lifecycle
  public async mounted(): Promise<void> {
    // Retrieve categories
    try {
      const response: AxiosResponse<ProductCategory[]> =
          await this.$http.get(this.$sfRouter.generate('app_api_product_categories'));
      this.categories = response.data
          .sort((a: ProductCategory, b: ProductCategory) => a.name.localeCompare(b.name));
    } catch (e) {
      // Don't mind it if no categories are found
      console.log(e);
    }

    this.isLoading = false;

    if (this.focusOnMount) {
      this.focusSearch();
    }
  }

  /**
   * Clear the list selection
   */
  public clearSelectedProduct(): void {
    this.productIsSelected = false;
    this.productList.clearSelected();
  }

  /**
   * Tell component that the product information has been hidden
   */
  public productInfoHidden(): void {
    this.productInfoShown = false;
  }

  public focusSearch(clear = false): void {
    this.productList.focusSearchField(clear);
  }

  // noinspection JSUnusedGlobalSymbols Vue lifecycle
  protected created(): void {
    if (this.rowActions) {
      this.fields.push({
        key: 'actions',
        label: this.$translator.trans('form.actions'),
        sortable: false,
        class: 'actions',
      });
    }
  }

  protected addProduct(): void {
    if (!this.forPurchase || !this.$isPurchaser) {
      return;
    }

    window.open(this.$sfRouter.generate('app_product_add'), '_blank');
  }

  @Watch('show', {immediate: true})
  protected onShow(): void {
    if (!this.show) {
      return;
    }

    this.$nextTick(() => {
      this.productList.focusSearchField();
    });
  }

  @Emit()
  protected cancel(): void {
    // Just emit event
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected productSelected(product: Product): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Just emit event
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected productGroupSelected(productGroup: ProductGroup): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Just emit event
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected editProduct(product: Product): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Just emit event
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected editProductGroup(productGroup: ProductGroup): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Just emit event
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected showProduct(product: Product): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    this.productInfoShown = true;
  }

  // noinspection JSUnusedLocalSymbols Used by emit
  @Emit()
  protected showProductGroup(productGroup: ProductGroup): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    this.productInfoShown = true;
  }

  protected onCancel(): void {
    if (this.productInfoShown) {
      return;
    }

    if (this.currentSearch.length === 0) {
      this.cancel();
    } else {
      this.focusSearch(true);
    }
  }

  protected openProductPage(): void {
    if (!this.$isEmployee || !this.currentProduct || this.productInfoShown) {
      return;
    }

    if (this.currentProduct._group) {
      this.showProductGroup(this.currentProduct);
    } else {
      this.showProduct(this.currentProduct);
    }
  }

  protected editProductPage(): void {
    if (!this.forPurchase || !this.$isPurchaser || !this.currentProduct) {
      return;
    }

    if (this.currentProduct._group) {
      this.editProductGroup(this.currentProduct);
    } else {
      this.editProduct(this.currentProduct);
    }
  }

  @Emit()
  protected importCsv(): void {
    // Just emit
  }

  protected onProductHighlighted(product: Product | ProductGroup | null): void {
    if (this.productIsSelected) {
      return;
    }

    this.currentProduct = product;
  }

  protected onProductSelected(product: Product | ProductGroup | null): void {
    if (product !== null) {
      this.productIsSelected = true;
      this.currentProduct = product;
      if (product._group) {
        this.productGroupSelected(product);
      } else {
        this.productSelected(product);
      }
    }
  }

  protected onProductDeselected(): void {
    this.productIsSelected = false;
  }

  protected get searchOptions(): { [key: string]: unknown } {
    return {g: this.includeProductGroups};
  }
}
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins/breakpoints";

@include media-breakpoint-down(md) {
  .product-info {
    display: none !important;
  }
}

.product-info {
  border-top: 1px solid $border-color;

  .row {
    > div {
      border-top: 1px solid $gray-300;
      line-height: 2;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.title {
        font-weight: bold;
      }

      &:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        padding-right: 0;
      }
    }

    &:first-of-type {
      > div {
        border-top: none;
      }
    }

    &:nth-of-type(even) {
      background-color: $table-accent-bg;
    }
  }

  .product-info-img {
    width: 20vw;
    height: 10rem;

    img {
      margin: auto;
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }
}
</style>
